import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';

interface UserRoleReleaseConfirmProps {
  open: boolean;
  email: string;
  currentRole: string;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  errorMessage?: string;
}

/**
 * 역할 해제 확인 다이얼로그.
 * PATCH /api/eraf/users/{id}/role { role: 'ROLE_USER' } 를 호출하기 전 확인을 받는다.
 * 대상 역할은 항상 ROLE_USER (일반 사용자) 로 고정된다.
 */
export function UserRoleReleaseConfirm({
  open,
  email,
  currentRole,
  onClose,
  onConfirm,
  isLoading = false,
  errorMessage,
}: UserRoleReleaseConfirmProps) {
  const { t } = useTranslation();

  const toRoleLabel = (role: string) =>
    t(`appshell.user.role.${role.replace('ROLE_', '').toLowerCase()}`, {
      defaultValue: role,
    });

  return (
    <FormDialog
      open={open}
      title={t('user.action.confirm.releaseRole.title')}
      onClose={onClose}
      onSubmit={onConfirm}
      submitLabel={
        isLoading
          ? t('user.action.confirm.ok') + '...'
          : t('user.action.confirm.ok')
      }
      cancelLabel={t('user.action.confirm.cancel')}
    >
      <div>
        {errorMessage && (
          <div
            role="alert"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background:
                'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          {t('user.action.confirm.releaseRole.description', {
            email,
            from: toRoleLabel(currentRole),
            to: toRoleLabel('ROLE_USER'),
          })}
        </p>
        <div
          style={{
            padding: 'var(--spacing-3)',
            background:
              'color-mix(in srgb, var(--color-warning) 8%, var(--color-surface-muted))',
            border: '1px solid var(--color-warning)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('user.action.confirm.releaseRole.warning')}
        </div>
      </div>
    </FormDialog>
  );
}
